import { useEffect, useId, useRef, type ReactNode } from "react";

import Icon from "./Icon";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/**
 * Generic <dialog>-based modal wrapper.
 *
 * Behavior parity with the legacy template + `new-entry.js`:
 *   - opens via `dialog.showModal()` when available (with the legacy
 *     `dialog.setAttribute("open", "")` fallback for older Safari builds);
 *   - clicking the backdrop closes — but only when the click target IS the
 *     dialog itself (not its inner panel);
 *   - ESC fires the native `cancel` event; we intercept it and call
 *     `onClose` so the parent decides whether to confirm.
 *   - The X button calls `onClose` too.
 *
 * The parent owns the open/closed state. `onClose` may be a no-op if the
 * parent wants to refuse the close (e.g. dirty-form confirm).
 */
export default function Modal({ isOpen, onClose, title, children }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const titleId = useId();

  // Open / close the underlying <dialog> in response to prop changes.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen) {
      if (!dialog.open) {
        if (typeof dialog.showModal === "function") dialog.showModal();
        else dialog.setAttribute("open", "");
      }
    } else if (dialog.open) {
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    }
  }, [isOpen]);

  // Intercept the native cancel (ESC) so the parent can confirm-then-close.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener("cancel", handleCancel);
    return () => dialog.removeEventListener("cancel", handleCancel);
  }, [onClose]);

  // Backdrop click → only when the click landed on the dialog element itself.
  const handleDialogClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) onClose();
  };

  // Don't render the heavy form subtree until the modal is opened — keeps
  // the initial overview render light and matches user expectation.
  return (
    <dialog
      ref={dialogRef}
      className="modal"
      aria-labelledby={titleId}
      onClick={handleDialogClick}
    >
      <div className="modal__panel">
        <header className="modal__header">
          <h2 className="modal__title" id={titleId}>
            {title}
          </h2>
          <button
            type="button"
            className="modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="x" />
          </button>
        </header>
        {isOpen ? children : null}
      </div>
    </dialog>
  );
}
